import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useDashboardStore } from "@/stores/dashboardStore";
import { authService } from "@/services/authService";
import { showToast } from "@/lib/toast";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  User,
  Shield,
  Settings,
  Users,
  FileText,
  MessageSquare,
  Image,
  Activity,
  Eye,
  Menu,
} from "lucide-react";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { permissions, isAdmin, getUserRole, canRead } = usePermissions();
  const {
    summary,
    isLoading: isDashboardLoading,
    fetchSummary,
  } = useDashboardStore();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserInfo();
    fetchSummary();
  }, [fetchSummary]);

  const fetchUserInfo = async () => {
    try {
      setIsLoading(true);
      const { accessToken } = useAuthStore.getState();
      if (accessToken) {
        const info = await authService.getUserInfo(accessToken);
        setUserInfo(info);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      showToast.error("Lỗi", "Không thể tải thông tin người dùng");
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation handlers
  const handleNavigateToUsers = () => {
    navigate(ROUTES.USERS);
  };

  const handleNavigateToArticles = () => {
    navigate(ROUTES.ARTICLES);
  };

  const handleNavigateToComments = () => {
    navigate(ROUTES.COMMENTS);
  };

  const handleNavigateToMenu = () => {
    navigate(ROUTES.MENUS);
  };

  const handleNavigateToRoles = () => {
    navigate(ROUTES.ROLES);
  };

  const handleNavigateToImages = () => {
    navigate(ROUTES.IMAGES);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatNumber = (num: number) => {
    return num.toLocaleString("vi-VN");
  };

  const formatGrowthPercent = (percent: number | null) => {
    if (percent === null) return "Chưa có dữ liệu";
    const sign = percent >= 0 ? "+" : "";
    return `${sign}${percent.toFixed(1)}%`;
  };

  const statsData = summary
    ? [
        {
          title: "Bài viết",
          value: formatNumber(summary.articles.count),
          unit: "bài viết",
          change: formatGrowthPercent(summary.articles.growthPercent),
          changeType:
            summary.articles.growthPercent === null
              ? "neutral"
              : summary.articles.growthPercent >= 0
              ? "increase"
              : "decrease",
          icon: FileText,
          color:
            summary.articles.growthPercent === null
              ? "text-gray-600"
              : summary.articles.growthPercent >= 0
              ? "text-blue-600"
              : "text-red-600",
          bgColor: "bg-blue-50",
        },
        {
          title: "Bình luận",
          value: formatNumber(summary.comments.count),
          unit: "bình luận",
          change: formatGrowthPercent(summary.comments.growthPercent),
          changeType:
            summary.comments.growthPercent === null
              ? "neutral"
              : summary.comments.growthPercent >= 0
              ? "increase"
              : "decrease",
          icon: MessageSquare,
          color:
            summary.comments.growthPercent === null
              ? "text-gray-600"
              : summary.comments.growthPercent >= 0
              ? "text-green-600"
              : "text-red-600",
          bgColor: "bg-green-50",
        },
        {
          title: "Hình ảnh",
          value: formatNumber(summary.images.count),
          unit: "hình ảnh",
          change: formatGrowthPercent(summary.images.growthPercent),
          changeType:
            summary.images.growthPercent === null
              ? "neutral"
              : summary.images.growthPercent >= 0
              ? "increase"
              : "decrease",
          icon: Image,
          color:
            summary.images.growthPercent === null
              ? "text-gray-600"
              : summary.images.growthPercent >= 0
              ? "text-purple-600"
              : "text-red-600",
          bgColor: "bg-purple-50",
        },
        {
          title: "Người dùng",
          value: formatNumber(summary.users.count),
          unit: "người dùng",
          change: formatGrowthPercent(summary.users.growthPercent),
          changeType:
            summary.users.growthPercent === null
              ? "neutral"
              : summary.users.growthPercent >= 0
              ? "increase"
              : "decrease",
          icon: Users,
          color:
            summary.users.growthPercent === null
              ? "text-gray-600"
              : summary.users.growthPercent >= 0
              ? "text-orange-600"
              : "text-red-600",
          bgColor: "bg-orange-50",
        },
      ]
    : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent mb-2">
                Chào mừng trở lại, {userInfo?.name || user?.name || "Admin"}! 👋
              </h1>
              <p className="text-blue-100">
                Đây là tổng quan về hoạt động của hệ thống hôm nay
              </p>
            </div>
            <div className="hidden md:block">
              <Activity className="h-24 w-24 text-blue-200 opacity-50" />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {isDashboardLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, index) => (
              <Card
                key={index}
                className="hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-24 mb-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : statsData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsData.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card
                  key={index}
                  className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {stat.title}
                        </p>
                        <div className="flex items-baseline space-x-2">
                          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {stat.value}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {stat.unit}
                          </p>
                        </div>
                        <p className={`text-sm font-medium ${stat.color} mt-1`}>
                          {stat.change} so với tháng trước
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                        <Icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-500 dark:text-gray-400">
                Không thể tải dữ liệu thống kê
              </p>
              <Button onClick={fetchSummary} className="mt-4" variant="outline">
                Thử lại
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Thông tin tài khoản
              </CardTitle>
              <CardDescription>
                Thông tin chi tiết về tài khoản của bạn
              </CardDescription>
            </CardHeader>
            <CardContent>
              {userInfo ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Tên người dùng
                      </label>
                      <p className="text-lg font-semibold">{userInfo.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Email
                      </label>
                      <p className="text-lg">{userInfo.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Vai trò
                      </label>
                      <p className="text-lg">
                        {getUserRole() ||
                          userInfo.role?.name ||
                          "Chưa có vai trò"}
                        {isAdmin() && (
                          <span className="ml-2 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            Admin
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        ID
                      </label>
                      <p className="text-sm font-mono">{userInfo._id}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  Không thể tải thông tin người dùng
                </p>
              )}
            </CardContent>
          </Card>

          {/* Permissions Card */}
          {permissions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Quyền hạn
                </CardTitle>
                <CardDescription>
                  Danh sách quyền hạn của bạn trong hệ thống
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {permissions.map((perm, index) => {
                    const subjectMap: Record<string, string> = {
                      all: "Tất cả",
                      log: "Nhật ký",
                      user: "Người dùng",
                      role: "Vai trò",
                      information: "Thông tin",
                      category: "Danh mục",
                      article: "Bài viết",
                      image: "Hình ảnh",
                      page: "Trang",
                      blog_post: "Blog Post",
                      menu: "Menu",
                      film_list: "Danh sách phim",
                      author: "Tác giả",
                      robot_txt: "Robot.txt",
                      widget: "Widget",
                      comment: "Bình luận",
                    };
                    const actionMap: Record<string, string> = {
                      create: "Tạo",
                      read: "Xem",
                      update: "Sửa",
                      delete: "Xóa",
                      manage: "Toàn quyền",
                    };
                    const subjectVi = subjectMap[perm.subject] || perm.subject;
                    const actionsVi = (perm.action || [])
                      .map((a) => actionMap[a] || a)
                      .join(", ");
                    const isFullAccess =
                      perm.subject === "all" &&
                      (perm.action || []).includes("manage");
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Shield className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">{subjectVi}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          ({actionsVi})
                        </span>
                        {isFullAccess && (
                          <span className="ml-auto px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            Toàn quyền
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Thao tác nhanh
            </CardTitle>
            <CardDescription>
              Các tính năng có sẵn trong hệ thống
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Quản lý người dùng - Chỉ admin */}
              {canRead("user") && (
                <Button
                  onClick={handleNavigateToUsers}
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <Users className="h-6 w-6" />
                  Quản lý người dùng
                </Button>
              )}

              {/* Quản lý bài viết */}
              {canRead("article") && (
                <Button
                  onClick={handleNavigateToArticles}
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <FileText className="h-6 w-6" />
                  Quản lý bài viết
                </Button>
              )}

              {/* Quản lý bình luận */}
              {canRead("comment") && (
                <Button
                  onClick={handleNavigateToComments}
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <MessageSquare className="h-6 w-6" />
                  Quản lý bình luận
                </Button>
              )}

              {/* Quản lý hình ảnh */}
              {canRead("image") && (
                <Button
                  onClick={handleNavigateToImages}
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <Image className="h-6 w-6" />
                  Quản lý hình ảnh
                </Button>
              )}

              {/* Cấu hình menu */}
              {canRead("menu") && (
                <Button
                  onClick={handleNavigateToMenu}
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <Menu className="h-6 w-6" />
                  Cấu hình menu
                </Button>
              )}

              {/* Quản lý vai trò - Chỉ admin */}
              {canRead("role") && (
                <Button
                  onClick={handleNavigateToRoles}
                  variant="outline"
                  className="h-20 flex flex-col items-center justify-center gap-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <Shield className="h-6 w-6" />
                  Quản lý vai trò
                </Button>
              )}
            </div>

            {/* Hiển thị thông báo nếu không có quyền nào */}
            {!canRead("user") &&
              !canRead("article") &&
              !canRead("comment") &&
              !canRead("image") &&
              !canRead("menu") &&
              !canRead("role") && (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Bạn chưa có quyền truy cập vào các tính năng quản trị
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Liên hệ admin để được cấp quyền
                  </p>
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
